import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

// Query keys
export const adminVendorServiceKeys = {
  all: ['admin', 'vendor-services'],
  lists: () => [...adminVendorServiceKeys.all, 'list'],
  list: (filters) => [...adminVendorServiceKeys.lists(), { filters }],
  details: () => [...adminVendorServiceKeys.all, 'detail'],
  detail: (id) => [...adminVendorServiceKeys.details(), id],
  statistics: () => [...adminVendorServiceKeys.all, 'statistics'],
};

// API functions
const adminVendorServiceApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/vendor-services', { params });
    return response.data;
  },

  getOne: async (id) => {
    const response = await api.get(`/admin/vendor-services/${id}`);
    return response.data;
  },

  getStatistics: async () => {
    const response = await api.get('/admin/vendor-services/statistics');
    return response.data;
  },

  toggleStatus: async (id) => {
    const response = await api.put(`/admin/vendor-services/${id}/toggle-status`);
    return response.data;
  },

  toggleFeatured: async (id) => {
    const response = await api.put(`/admin/vendor-services/${id}/toggle-featured`);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/vendor-services/${id}`);
    return response.data;
  },
};

const invalidateFeaturedPublic = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['vendors', 'featured'] });
  queryClient.invalidateQueries({ queryKey: ['vendors'] });
};

export const useAdminVendorServices = (params = {}, options = {}) =>
  useQuery({
    queryKey: adminVendorServiceKeys.list(params),
    queryFn: () => adminVendorServiceApi.getAll(params),
    ...options,
  });

export const useAdminVendorService = (id, options = {}) =>
  useQuery({
    queryKey: adminVendorServiceKeys.detail(id),
    queryFn: () => adminVendorServiceApi.getOne(id),
    enabled: Boolean(id),
    ...options,
  });

export const useAdminVendorServiceStatistics = (options = {}) =>
  useQuery({
    queryKey: adminVendorServiceKeys.statistics(),
    queryFn: adminVendorServiceApi.getStatistics,
    ...options,
  });

export const useToggleVendorServiceStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminVendorServiceApi.toggleStatus,
    onSuccess: (data, serviceId) => {
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.detail(serviceId) });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.statistics() });
      invalidateFeaturedPublic(queryClient);
      options.onSuccess?.(data);
    },
    onError: (error) => options.onError?.(error),
  });
};

export const useToggleVendorServiceFeatured = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminVendorServiceApi.toggleFeatured,
    onSuccess: (data, serviceId) => {
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.detail(serviceId) });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.statistics() });
      invalidateFeaturedPublic(queryClient);
      options.onSuccess?.(data);
    },
    onError: (error) => options.onError?.(error),
  });
};

export const useDeleteVendorService = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminVendorServiceApi.delete,
    onSuccess: (data, serviceId) => {
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.detail(serviceId) });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.statistics() });
      invalidateFeaturedPublic(queryClient);
      options.onSuccess?.(data);
    },
    onError: (error) => options.onError?.(error),
  });
};
